'use client'

import EventCardImageTitle from '@/components/_cards/event/driver-calendar/event-card-image-title'
import ImageWithFallback from '@/components/common/image-with-fallback'
import TrophyIcon from '@/components/icons/trophy'
import { localDateTime } from '@/helpers/datetime'
import { getMainSession } from '@/helpers/events'
import { getUserProfilePictureUrl } from '@/helpers/user'
import apiFetch from '@/store/helpers/api-fetch'
import { useEffect, useState } from 'react'
import EventCardStartTime from './event-card-start-time'

export default function EventCardPrevious({ previousEvent, games, user }) {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)

  let previousEventMainSession = null
  let previousEventGame = null
  let previousEventStart = null

  if (previousEvent?.sessions && previousEvent?.sessions.length != 0) {
    previousEventMainSession = getMainSession({ event: previousEvent })
    const track = previousEventMainSession?.track
    if (track) {
      previousEventGame = games.find((s) => s.name === track.gameName)
    }

    previousEventStart = localDateTime({
      datetime: previousEventMainSession?.targetDateTime,
      timezone: previousEventMainSession?.targetTimezone,
      format: false,
    })
  }

  useEffect(() => {
    getSessionResults()
  }, [])

  async function getSessionResults() {
    if (previousEventMainSession != null) {
      try {
        const results = await apiFetch({
          url: `community/${previousEvent.championship.community.id}/championship/${previousEvent.championshipId}/event/${previousEventMainSession.eventId}/session/${previousEventMainSession.id}/result`,
        })

        // Check if results is array before iterating
        if (Array.isArray(results)) {
          results.forEach((res) => {
            if (res.userId === user.id) {
              setData(res)
            }
          })
        }
      } catch (error) {
        // fail silently
      }

      setIsLoading(false)
    } else {
      setIsLoading(false)
    }
  }

  function ordinalSuffixOf(i) {
    let j = i % 10,
      k = i % 100
    if (j === 1 && k !== 11) {
      return i + 'st'
    }
    if (j === 2 && k !== 12) {
      return i + 'nd'
    }
    if (j === 3 && k !== 13) {
      return i + 'rd'
    }
    return i + 'th'
  }

  if (!previousEvent || !previousEvent.sessions) return null

  return (
    <div>
      <div className="flex items-center">
        <div className="mr-4 w-fit self-center">
          <div className="relative flex h-11 w-11 overflow-hidden rounded-full">
            <ImageWithFallback
              alt="Profile pic"
              src={getUserProfilePictureUrl(user, {
                profile: 'driver',
              })}
              fallbackUrl="/img/default/profile-pic.webp"
              fill
            />
          </div>
        </div>
        <div className="font-inter text-p-2">
          <span className="text font-bold">{user.username}</span>
          <span className="text-neutral-blue-500"> finished the</span>
          <span className="font-semibold"> {previousEvent.name} event</span>
          {!isLoading && data?.racePosition ? (
            <span className="font-bold">
              <span className="text-neutral-blue-500"> in </span>
              <span
                className={
                  data?.racePosition === 1
                    ? 'text-[#FF9900]'
                    : data?.racePosition === 2
                      ? 'text-[#FECACA]'
                      : data?.racePosition === 3
                        ? 'text-[#FF7222]'
                        : 'text-white'
                }
              >
                {ordinalSuffixOf(data?.racePosition)}
              </span>
            </span>
          ) : null}

          {previousEventGame && (
            <>
              <span className="text-neutral-blue-500"> on </span>
              <span className="font-semibold text-white">
                {previousEventGame.nicename}
              </span>
            </>
          )}
          {previousEventMainSession?.targetDateTime &&
          previousEventMainSession?.targetTimezone ? (
            <span className="text-neutral-blue-500">
              {previousEventStart.format(' dddd DD MMMM ')} at{' '}
              {previousEventStart.format(' H:mm')}{' '}
              {previousEventStart.format('z')}
            </span>
          ) : null}
          {!previousEvent?.championship?.isSingleEvent ? (
            <>
              <span className="text-neutral-blue-500"> hosted by </span>
              <span className="font-semibold text-white">
                {previousEvent.championship.name}
              </span>
            </>
          ) : null}
        </div>
      </div>

      <div className="mt-4 rounded-md border border-neutral-blue-700 bg-neutral-blue-900 font-inter">
        <div className="flex w-full flex-col p-4 desktop-lg:flex-row">
          <EventCardImageTitle
            event={previousEvent}
            EventMainSession={previousEventMainSession}
          />

          {!isLoading && data?.racePosition ? (
            <div className="mt-4 flex h-auto w-full flex-row-reverse justify-between gap-[32px] desktop-sm:justify-normal desktop-lg:mt-0 desktop-lg:w-[455px]">
              {previousEventStart ? (
                <div className="flex h-full w-full rounded-md border border-neutral-blue-700 bg-neutral-blue-800 px-[5px] py-6">
                  <div className="relative mx-auto my-auto flex flex-col self-center align-middle">
                    {data?.racePosition === 1 ? (
                      <div className="self-center text-[22px] font-bold text-medals-gold">
                        <TrophyIcon
                          className={'mx-auto h-12 w-12 stroke-medals-gold'}
                        ></TrophyIcon>
                        <div className="mt-[4px]">
                          {ordinalSuffixOf(data?.racePosition)} Place
                        </div>
                      </div>
                    ) : data?.racePosition === 2 ? (
                      <div className="self-center text-[22px] font-bold text-shades-0/80">
                        <TrophyIcon
                          className={'mx-auto h-12 w-12 stroke-shades-0/80'}
                        ></TrophyIcon>
                        <div className="mt-[4px]">
                          {ordinalSuffixOf(data?.racePosition)} Place
                        </div>
                      </div>
                    ) : data?.racePosition === 3 ? (
                      <div className="self-center text-[22px] font-bold text-medals-bronze">
                        <TrophyIcon
                          className={'mx-auto h-12 w-12 stroke-medals-bronze'}
                        ></TrophyIcon>
                        <div className="mt-[4px]">
                          {ordinalSuffixOf(data?.racePosition)} Place
                        </div>
                      </div>
                    ) : (
                      <div className="items-center self-center font-inter text-[22px] font-bold text-white">
                        <div className="mx-auto flex h-[50px] w-[50px] self-center rounded-full bg-white text-center text-black">
                          <div className="m-auto text-center">
                            {data?.racePosition < 10 ? <>0</> : null}
                            {data?.racePosition}
                          </div>
                        </div>
                        <div className="mt-[8px] text-center">
                          {ordinalSuffixOf(data?.racePosition)} Place
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <EventCardStartTime timeIn={previousEventStart} />
          )}
        </div>
      </div>
    </div>
  )
}
